import { createContext, useState, useEffect } from "react";
// import useLocalStorage from "../hooks/useLocalStorage";
import { AuthContextType } from "../types/contexts/AuthContext";
import { refreshTokens } from "../services/auth.service";
import useLocalStorage from "../hooks/useLocalStorage";

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isLoading: false,
  user: null,
  updateAuthentication: () => {},
  updateLoading: () => {},
  logout: () => {},
});

const AuthProvider: any = ({ children }: any) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [refreshToken, setRefreshToken] = useLocalStorage<string>("refresh-token");
  const [, setAccessToken] = useLocalStorage<string>("access-token");
  const [user, setUser] = useLocalStorage<any>("user");
  const [isLoading, setIsLoading] = useState<boolean>(true); // Set initial value to true

  const updateAuthentication = (authenticated: any) => {
    setIsAuthenticated(authenticated);
    setIsLoading(false);
  };

  const logout = () => {
    const rememberedLogin = localStorage.getItem("remembered-login");
    localStorage.clear();
    if (rememberedLogin) {
      localStorage.setItem("remembered-login", rememberedLogin);
    }
    setRefreshToken(null);
    setAccessToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setIsLoading(false);
  };

  const updateLoading = (isLoading: boolean) => {
    setIsLoading(isLoading);
  };

  const decodeJwtPayload = (token: string) => {
    try {
      const [, payload] = token.split(".");
      if (!payload) return null;
      return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    } catch {
      return null;
    }
  };

  const isJwtTokenValid = (token: string | null) => {
    if (!token) {
      return false;
    }

    const payload = decodeJwtPayload(token);
    if (!payload || typeof payload.exp !== "number") {
      return false;
    }

    return payload.exp * 1000 > Date.now();
  };

  useEffect(() => {
    const refreshSession = async () => {
      if (!refreshToken || !isJwtTokenValid(refreshToken)) {
        logout();
        return;
      }

      try {
        const response = await refreshTokens({ refreshToken });
        setRefreshToken(response.data.tokens.refresh.token);
        if (response.data.tokens?.access?.token) {
          setAccessToken(response.data.tokens.access.token);
        }
        setIsAuthenticated(true);
      } catch {
        logout();
      } finally {
        setIsLoading(false);
      }
    };

    refreshSession();
  }, []);

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, isLoading, user, updateAuthentication, updateLoading, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };
